import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Routes, Route } from 'react-router-dom';
// Context and hook imports
import { OceanDataProvider, useOcean } from './contexts/OceanDataContext';


// Component imports
import Header from './components/layout/Header';
import ControlPanel from './components/panels/ControlPanel';
import MapContainer from './components/map/MapContainer';

import OutputModule from './components/panels/OutputModule';
import OutputPanelResizeHandle from './components/panels/OutputPanelResizeHandle';
import PasswordProtect from './components/admin/PasswordProtect';
import EncryptedStorage from './services/encryptedStorageService';
import { useOutputPanelWidth } from './hooks/useOutputPanelWidth';

// Tutorial imports
import Tutorial from './components/tutorial/Tutorial';
import TutorialOverlay from './components/tutorial/TutorialOverlay';
import { TUTORIAL_STEPS } from './components/tutorial/TutorialSteps';

// CSS imports
import 'mapbox-gl/dist/mapbox-gl.css';

// Helper function for tutorial targeting
const getTutorialTarget = (currStepIndex) => {
  if (currStepIndex < 0 || currStepIndex >= TUTORIAL_STEPS.length) return null;
  return TUTORIAL_STEPS[currStepIndex].target || null;
};

/**
 * This component contains the main layout and view of the platform.
 * It consumes the ocean data from the context provided by OceanDataProvider.
 */
const OceanPlatform = () => {
  const [showApiConfig, setShowApiConfig] = useState(false);
  const mapOutputSectionRef = useRef(null);
  const {
    width: outputPanelWidth,
    isResizing: isOutputPanelResizing,
    isCollapsed: isOutputCollapsed,
    toggleCollapse: toggleOutputPanel,
    onResizeStart: onOutputPanelResizeStart
  } = useOutputPanelWidth(mapOutputSectionRef);
  // Consume the unified ocean data from the context
  const oceanData = useOcean();

  const scenarioCommsMapStep = oceanData.mapLayerVisibility?.exerciseScenario
    ? oceanData.scenarioCommsPlaybackStep
    : 0;

  // Check API status on component mount
  useEffect(() => {
    if (oceanData.connectionStatus && oceanData.connectionStatus.checkAPIStatus) {
      oceanData.connectionStatus.checkAPIStatus();
    }
  }, [oceanData.connectionStatus]);

  // Show API config panel if API is not connected and we have data
  useEffect(() => {
    if (oceanData.dataLoaded && oceanData.connectionStatus && !oceanData.connectionStatus.connected && !showApiConfig) {
      if (!oceanData.connectionStatus.hasApiKey && oceanData.connectionStatus.endpoint) {
        setShowApiConfig(true);
      }
    }
  }, [oceanData.dataLoaded, oceanData.connectionStatus, showApiConfig]);

  // Note: The global Loading and Error screens are now handled inside OceanDataProvider
  // so we don't need to render them here.

  return (
    <div className="bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white min-h-screen dark:bg-slate-900 dark:text-white">
      <Header
        dataSource={oceanData.dataSource}
        timeZone={oceanData.timeZone}
        onTimeZoneChange={oceanData.setTimeZone}
        connectionStatus={oceanData.connectionStatus}
        dataQuality={oceanData.dataQuality}
        showDataStatus={true}
        showTutorial={oceanData.showTutorial}
        onTutorialToggle={oceanData.handleTutorialToggle}
        tutorialStep={oceanData.tutorialStep}
        isFirstTimeUser={oceanData.isFirstTimeUser}
        apiStatus={oceanData.connectionStatus}
        apiMetrics={oceanData.connectionDetails}
        chatMetrics={oceanData.chatMessages?.length || 0}
        onShowApiConfig={() => setShowApiConfig(true)}
        onResetApiMetrics={() => { }}
      />

      <main className="flex-1 flex flex-col min-h-0 bg-white dark:bg-slate-900">
        <section className="border-b border-slate-200 dark:border-slate-700 flex-shrink-0 dark:border-pink-500/30">
          <ControlPanel
            data-tutorial="control-panel"
            isLoading={oceanData.isLoading}
            availableModels={oceanData.availableModels}
            availableDepths={oceanData.availableDepths}
            availableDatasources={oceanData.availableDatasources}
            hasDepthData={oceanData.hasDepthData}
            hasModelData={oceanData.hasModelData}
            dataLoaded={oceanData.dataLoaded}
            selectedArea={oceanData.selectedArea}
            selectedDepth={oceanData.selectedDepth}
            startDate={oceanData.startDate}
            endDate={oceanData.endDate}
            timeZone={oceanData.timeZone}
            currentFrame={oceanData.currentFrame}
            isPlaying={oceanData.isPlaying}
            playbackSpeed={oceanData.playbackSpeed}
            loopMode={oceanData.loopMode}
            availableDates={oceanData.availableDates}
            availableTimes={oceanData.availableTimes}
            totalFrames={oceanData.totalFrames}
            data={oceanData.data}
            frameTimelineData={oceanData.storySeries}
            mapLayerVisibility={oceanData.mapLayerVisibility}
            isSstHeatmapVisible={oceanData.isSstHeatmapVisible}
            currentsVectorScale={oceanData.currentsVectorScale}
            currentsColorBy={oceanData.currentsColorBy}
            onAreaChange={oceanData.setSelectedArea}
            viewMode={oceanData.viewMode}
            onViewModeChange={oceanData.setViewMode}
            vesselMarkerDisplay={oceanData.vesselMarkerDisplay}
            onVesselMarkerDisplayChange={oceanData.setVesselMarkerDisplay}
            // onModelChange={oceanData.setSelectedModel} // Removed or kept if logic still needs it? 
            // The underlying hook still has setSelectedModel, but UI is gone.
            onDepthChange={oceanData.setSelectedDepth}
            onDateRangeChange={oceanData.onDateRangeChange}
            onTimeZoneChange={oceanData.setTimeZone}
            onPlayToggle={oceanData.handlePlayToggle}
            onSpeedChange={oceanData.setPlaybackSpeed}
            onLoopModeChange={oceanData.setLoopMode}
            onFrameChange={oceanData.handleFrameChange}
            onReset={oceanData.handleReset}
            onSquery={oceanData.refreshData}
            onLayerToggle={oceanData.toggleMapLayer}
            onSstHeatmapToggle={oceanData.toggleSstHeatmap}
            vesselsById={oceanData.vesselsById}
            selectedVesselId={oceanData.selectedVesselId}
            onSelectedVesselChange={oceanData.setSelectedVesselId}
            threatLevelFilter={oceanData.threatLevelFilter}
            onThreatLevelFilterChange={oceanData.setThreatLevelFilter}
            vesselTypeFilter={oceanData.vesselTypeFilter}
            onVesselTypeFilterChange={oceanData.setVesselTypeFilter}
            vesselTimeRange={oceanData.vesselTimeRange}
            vesselPlaybackTime={oceanData.vesselPlaybackTime}
            onVesselPlaybackTimeChange={oceanData.setVesselPlaybackTime}
            onCurrentsScaleChange={oceanData.handleCurrentsScaleChange}
            onCurrentsColorChange={oceanData.handleCurrentsColorChange}
            heatmapScale={oceanData.heatmapScale}
            onHeatmapScaleChange={oceanData.onHeatmapScaleChange}
            apiStatus={oceanData.connectionStatus}
            // Wind Velocity Particle Configuration
            windVelocityParticleCount={oceanData.windVelocityParticleCount}
            onWindVelocityParticleCountChange={oceanData.onWindVelocityParticleCountChange}
            windVelocityParticleOpacity={oceanData.windVelocityParticleOpacity}
            onWindVelocityParticleOpacityChange={oceanData.onWindVelocityParticleOpacityChange}
            windVelocityParticleSpeed={oceanData.windVelocityParticleSpeed}
            onWindVelocityParticleSpeedChange={oceanData.onWindVelocityParticleSpeedChange}
          />
        </section>

        <section
          ref={mapOutputSectionRef}
          className="grid h-96 md:h-[500px] lg:h-[600px] min-h-0 overflow-hidden bg-white dark:bg-slate-900"
          style={{ gridTemplateColumns: `minmax(0, 1fr) ${outputPanelWidth}px` }}
        >
          <div className="relative min-h-0 h-full min-w-0 overflow-hidden bg-[#0f172a]">
            <MapContainer
              data-tutorial="map-container"
              stationData={oceanData.stationData}
              timeSeriesData={oceanData.timeSeriesData}
              rawData={oceanData.rawData}
              vesselsById={oceanData.vesselsById}
              selectedVesselId={oceanData.selectedVesselId}
              threatLevelFilter={oceanData.threatLevelFilter}
              vesselTypeFilter={oceanData.vesselTypeFilter}
              currentsGeoJSON={oceanData.currentsGeoJSON}
              currentFrame={oceanData.currentFrame}
              selectedDepth={oceanData.selectedDepth}
              selectedArea={oceanData.selectedArea}
              onDepthChange={oceanData.setSelectedDepth}
              onStationSelect={oceanData.setSelectedStation}
              onEnvironmentUpdate={oceanData.setEnvData}
              currentDate={oceanData.currentDate}
              currentTime={oceanData.currentTime}
              mapLayerVisibility={oceanData.mapLayerVisibility}
              isSstHeatmapVisible={oceanData.isSstHeatmapVisible}
              currentsVectorScale={oceanData.currentsVectorScale}
              currentsColorBy={oceanData.currentsColorBy}
              heatmapScale={oceanData.heatmapScale}
              layerAnimationEnabled={oceanData.layerAnimationEnabled}
              mapboxToken={process.env.REACT_APP_MAPBOX_ACCESS_TOKEN}
              isOutputCollapsed={isOutputCollapsed}
              outputPanelWidth={outputPanelWidth}
              isOutputPanelResizing={isOutputPanelResizing}
              availableDepths={oceanData.availableDepths}
              dataBounds={oceanData.dataBounds}
              viewMode={oceanData.viewMode}
              vesselMarkerDisplay={oceanData.vesselMarkerDisplay}
              // Wind Velocity Particle Configuration
              windVelocityParticleCount={oceanData.windVelocityParticleCount}
              windVelocityParticleOpacity={oceanData.windVelocityParticleOpacity}
              windVelocityParticleSpeed={oceanData.windVelocityParticleSpeed}
              isPlaying={oceanData.isPlaying}
              playbackSpeed={oceanData.playbackSpeed}
              loopMode={oceanData.loopMode}
              onPausePlayback={oceanData.pauseAnimation}
              playbackTime={oceanData.vesselPlaybackTime}
              setPlaybackTime={oceanData.setVesselPlaybackTime}
              scenarioCommsVisibleStep={scenarioCommsMapStep}
              playbackResetSerial={oceanData.playbackResetSerial}
            />
          </div>

          <div className="relative z-10 min-h-0 h-full min-w-0 overflow-hidden bg-white shadow-[-2px_0_0_0_#ffffff] dark:bg-slate-900 dark:shadow-[-2px_0_0_0_#0f172a]">
            <OutputPanelResizeHandle onResizeStart={onOutputPanelResizeStart} />
            <OutputModule
              timeSeriesData={oceanData.timeSeriesData}
              currentFrame={oceanData.currentFrame}
              selectedDepth={oceanData.selectedDepth}
              isCollapsed={isOutputCollapsed}
              onToggleCollapse={toggleOutputPanel}
              data={oceanData.data}
              dataSource={oceanData.dataSource}
              availableDepths={oceanData.availableDepths}
              selectedArea={oceanData.selectedArea}
              selectedModel={oceanData.selectedModel}
              playbackSpeed={oceanData.playbackSpeed}
              envData={oceanData.envData}
              timeZone={oceanData.timeZone}
              startDate={oceanData.startDate}
              endDate={oceanData.endDate}
              dataLoaded={oceanData.dataLoaded}
              isPlaying={oceanData.isPlaying}
            />
          </div>
        </section>


      </main>

      <Tutorial
        isOpen={oceanData.showTutorial}
        onClose={oceanData.handleTutorialToggle}
        onComplete={oceanData.handleTutorialComplete}
        onSkipTour={oceanData.handleTutorialComplete}
        tutorialStep={oceanData.tutorialStep}
        onStepChange={oceanData.handleTutorialStepChange}
      />

      <TutorialOverlay
        isActive={oceanData.showTutorial}
        targetSelector={getTutorialTarget(oceanData.tutorialStep)}
        highlightType="spotlight"
        showPointer={oceanData.tutorialStep > 0}
      />
    </div>
  );
};

/**
 * Main application entry point.
 * Wraps the entire platform in the OceanDataProvider to provide global state.
 * Conditionally renders the main app or a login screen based on authentication status.
 */
const App = () => {
  return (
    <Routes>
      <Route path="/*" element={<MainApp />} />
    </Routes>
  );
};

const MainApp = () => {
  const [isPasswordAuthenticated, setIsPasswordAuthenticated] = useState(false);

  // Load persisted password-authenticated flag on first mount
  useEffect(() => {
    try {
      const stored = EncryptedStorage.getItem('ocean-password-auth');
      if (stored === true) {
        setIsPasswordAuthenticated(true);
      }
    } catch (error) {
      console.warn('Failed to read persisted password auth state:', error);
    }
  }, []);

  const handlePasswordSuccess = useCallback(() => {
    setIsPasswordAuthenticated(true);
    try {
      EncryptedStorage.setItem('ocean-password-auth', true);
    } catch (error) {
      console.warn('Failed to persist password auth state:', error);
    }
  }, []);

  // If authenticated via method (password), show main app
  if (isPasswordAuthenticated) {
    return (
      <OceanDataProvider authMethod={'password'}>
        <OceanPlatform />
      </OceanDataProvider>
    );
  }

  // If not authenticated by either method, show login options
  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white dark:bg-slate-900 dark:text-white">
      <h1 className="text-3xl font-bold mb-8">Welcome to the Oceanographic Platform</h1>

      <div className="w-full max-w-md space-y-8">
        {/* Password Login Option */}
        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 p-6 dark:bg-slate-800 dark:border-pink-500/30">
          <h2 className="text-xl font-semibold mb-4 text-center text-slate-800 dark:text-white">Login with Password</h2>
          <PasswordProtect onSuccess={handlePasswordSuccess} inline={true} />
        </div>


      </div>
    </div>
  );
}

export default App;